import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { useUserSecurityGroupsQuery } from '@/features/security-admin/hooks/useUserSecurityGroupsQuery';
import { Alert, Button } from '@shared/components';
import { AssignUserToGroupsModal } from '@/features/security-admin/components/AssignUserToGroupsModal';
import { UserPermissionsEditor } from '@/features/security-admin/components/UserPermissionsEditor';
import { UserSecurityGroupDataTable } from './UserSecurityGroupDataTable';
import type { BackRef } from '@/features/smr-admin/types/backRef';
import type { SecurityGroupRecord } from '@/features/security-admin/types/security-admin.types';

interface Props {
  userId: string;
  userName: string;
}

export function UserSecurityGroupsPanel({ userId, userName }: Props) {
  return (
    <PermissionGuard
      permission={{ resource: 'user', action: 'read' }}
      showFallback
      fallback={
        <p className="text-sm text-mp-muted">
          You do not have permission to view this user's security groups.
        </p>
      }
    >
      <UserSecurityGroupsPanelBody userId={userId} userName={userName} />
    </PermissionGuard>
  );
}

function UserSecurityGroupsPanelBody({ userId, userName }: Props) {
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SecurityGroupRecord | null>(null);
  const groupsQuery = useUserSecurityGroupsQuery(userId);
  const groups = groupsQuery.data ?? [];

  const backRef: BackRef = { path: `/admin/users/${userId}`, label: userName };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Security Groups</h3>
        <PermissionGuard permission={{ resource: 'security_group', action: 'manage_users' }}>
          <Button onClick={() => setAssignModalOpen(true)}>Assign Security Groups</Button>
        </PermissionGuard>
      </div>

      {groupsQuery.isLoading ? (
        <p className="text-sm text-mp-muted">Loading security groups...</p>
      ) : groupsQuery.isError ? (
        <Alert variant="error">
          <span>Failed to load security groups.</span>
        </Alert>
      ) : groups.length === 0 ? (
        <p className="text-sm text-mp-muted">No security groups assigned.</p>
      ) : (
        <UserSecurityGroupDataTable data={groups} backRef={backRef} onEditPermissions={setEditingGroup} />
      )}

      {isAssignModalOpen && (
        <AssignUserToGroupsModal
          isOpen={isAssignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          userId={userId}
          userName={userName}
        />
      )}

      {editingGroup && (
        <UserPermissionsEditor
          groupId={editingGroup.id}
          userId={userId}
          existingPermissions={editingGroup.user_permissions?.[0]}
          existingDealerPermissions={editingGroup.user_dealer_permissions ?? []}
          groupDealers={editingGroup.dealers ?? []}
          invalidateUserId={userId}
          onClose={() => setEditingGroup(null)}
        />
      )}
    </div>
  );
}
